<template>
  <inline-loader v-if="isLoading" />
  <div v-else class="form-group tag-content resource-tagging-selection">
    <!-- syllabus -->
    <dl class="field-set type-dropdown syllabus">
      <dt class="field-label">{{ syllabusLabel }}</dt>
      <dd class="field-input">
        <combo-box
          :auto-filter="true"
          :options="contentMapSelections"
          :value="syllabusSelectedUI"
          :label="syllabusPlaceholder"
          :disabled="isSyllabusDisabled"
          @change="handleChangeSyllabus"
        />
      </dd>
    </dl>

    <cv-inline-notification
      v-if="showContentMapWarning"
      class="content-map-warning"
      kind="info"
      :title="contentMapWarningTitle"
      :sub-title="contentMapWarningSubtitle"
      low-contrast
      hide-close-button
    />

    <!-- cognitive level -->
    <dl v-if="showCognitiveLevelSection" class="field-set type-dropdown cognitive-level">
      <dt class="field-label">{{ t("resource_tagging_label_cognitive_level") }}</dt>
      <dd class="field-input">
        <combo-box
          v-show="showCognitiveLevel"
          :auto-filter="true"
          :options="filterCognitiveLevel"
          :label="cognitiveLevelPlaceHolder"
          :value="cognitiveLevelSelectedUI"
          @change="handleChangeCognitiveLevel"
        />
        <combo-box
          v-show="!showCognitiveLevel"
          :options="filterCognitiveLevel"
          :label="cognitiveLevelPlaceHolder"
          :disabled="true"
        />
      </dd>
    </dl>
    <!-- topic/sub-topics -->
    <dl class="field-set topic tree" data-testid="resource-tagging-topics">
      <dt class="field-label">{{ topicsLabel }}</dt>
      <dd class="field-input">
        <div v-if="!isAdaptiveSection">
          <div class="info">{{ t("resource_tagging_instruction_tree") }}</div>
          <div class="als-recommendation-legend">
            <svg-icon name="LearningOutcomeAssistant24" kind="indicative" />
            <span>{{ t("content_map_available_for_als_recommendation_legend_label") }}</span>
          </div>
        </div>
        <!-- tree -->
        <inline-loader v-if="isSyllabusSelected && treeIsLoading" />
        <div v-else-if="isSyllabusSelected && !isTopicsEmpty" class="tree-wrapper">
          <tree-structure
            ref="tree"
            :property-names="propertyNames"
            :data="topicsProcessed"
            :use-check-box="useCheckBox"
            :use-radio="useRadio"
            :selected-radio-node-id="selectedRadioNodeId"
            :editable="treeEditable"
            :is-plain-text-node="false"
            :only-leaf-selectable="onlyLeafSelectable"
            :show-node-name="showLoName"
            :show-node-name-toggle="hasAtLeastOneLoWithName"
            :node-name-label="loNameToggleLabel"
            :node-name-tooltip="loNameToggleTooltip"
            @node-name-toggled="toggleLoName"
            @node_checked="handleChangeTopics"
            @node_unchecked="handleChangeTopics"
          />
        </div>
        <div v-else class="tree-wrapper empty">
          <div class="message">
            {{ t("resource_tagging_placeholder_tree") }}
          </div>
        </div>
      </dd>
    </dl>
  </div>
</template>

<script setup lang="ts">
import resourceTaggingStore from "@modules/resource-tagging-selection/components/ResourceTaggingSelectionStore";
import contentMapApi from "@/apis/content.map.api";
import {
  CONTENT_MAP_WARNING_CONTEXT,
  RESOURCE_TAGGING_RESOURCE_TYPE,
  type ContentMapWarningContext,
} from "@modules/resource-tagging-selection/service/resource.tagging.selection.constant";
import treeUtils from "@plugins/tree-structure/tree.utils";
import { isDefined, isEmpty, isNull } from "@/utils/var.check";
import resourceTaggingsStore from "@modules/editor/components/resource-taggings/ResourceTaggingsStore";
import sortUtils from "@utils/sort.utils";
import { loadLocaleMessages } from "@plugins/i18n";
import { useLearningOutcomeToggle } from "@modules/content-map/components/composables/learning-outcome-toggle.ts";
import { checkIfAtLeastOneNodeHasName } from "@modules/content-map/components/services/learning-outcome-service.ts";
import { computed, nextTick, onBeforeMount, ref, useTemplateRef, watch, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@/plugins/modal/composables/modal-instance";
import { useErrorHandler } from "@/plugins/common/composables/error";

// Props
const props = defineProps({
  resourceTagging: { type: Object, default: null },
  resourceType: { type: String, default: "" },
  taggingSelection: { type: Object, default: () => ({}) },
  allowMultipleSelectTopic: { type: Boolean, default: true },
  isAdaptiveSection: { type: Boolean, default: false },
  contentMapWarningContext: {
    type: String as PropType<ContentMapWarningContext>,
    default: undefined,
  },
});

// Emits
const emit = defineEmits(["update:taggingSelection", "ChangeSelection", "UpdateTagging"]);

// Data
const taggingSelectionLocal = ref(props.taggingSelection);
const isLoading = ref(true);
const syllabusSelectedUI = ref("");
const cognitiveLevelSelectedUI = ref("");
const propertyNames = ref({ text: "title" });
const treeIsLoading = ref(true);
const treeEditable = ref(false);
const topics = ref([]);
const topicsProcessed = ref([]);
const cognitiveLevels = ref([]);
const topicsIsDirty = ref(false);
const cognitiveLevelIsDirty = ref(false);
const reload = ref("");
const taggingOri = ref({});
const hasAtLeastOneLoWithName = ref(false);
const selectedContentMapUsage = ref<{
  usedInLearningProgress: boolean;
  usedInMoeLibrary: boolean;
} | null>(null);

// Composables
const modal = useModal();
const { t } = useI18n();
const treeRef = useTemplateRef("tree");

const { passiveApiFailHandler } = useErrorHandler();
const { showLoName, toggleLoName, loNameToggleLabel, loNameToggleTooltip } =
  useLearningOutcomeToggle();

// Data properties
const resourceTaggingSelectionStore = ref(resourceTaggingsStore);
const taggingStore = ref(resourceTaggingStore);

const resourceUuid = computed(() => {
  return props.resourceTagging.tagId.split("_")[0];
});

const isSyllabusDisabled = computed(() => {
  return contentMapSelections.value.length === 0;
});

const filterCognitiveLevel = computed(() => {
  if (!cognitiveLevels.value.length) return [];
  const filteredCognitiveLevel = cognitiveLevels.value.map((cognitiveLevel) => {
    return {
      name: cognitiveLevel.text,
      label: cognitiveLevel.text,
      value: cognitiveLevel.id.toString(),
    };
  });
  filteredCognitiveLevel.unshift({
    name: t("resource_tagging_placeholder_cognitive_level"),
    label: t("resource_tagging_placeholder_cognitive_level"),
    value: "-1",
  });
  return filteredCognitiveLevel;
});

const syllabusPlaceholder = computed(() => {
  return t("resource_tagging_placeholder_syllabus");
});

const cognitiveLevelPlaceHolder = computed(() => {
  return t("resource_tagging_placeholder_cognitive_level");
});

const contentMapMetadata = computed(() => {
  return contentMapMetadataSelected.value ? contentMapMetadataSelected.value : {};
});

const isSyllabusSelected = computed(() => {
  return !isEmpty(syllabusSelected.value);
});

const showContentMapWarning = computed(() => {
  if (!selectedContentMapUsage.value) {
    return false;
  }

  switch (props.contentMapWarningContext) {
    case CONTENT_MAP_WARNING_CONTEXT.LEARNING_PROGRESS:
      return selectedContentMapUsage.value.usedInLearningProgress === false;
    case CONTENT_MAP_WARNING_CONTEXT.MOE_LIBRARY:
      return selectedContentMapUsage.value.usedInMoeLibrary === false;
    default:
      return false;
  }
});

const contentMapWarningTitle = computed(() => {
  return props.contentMapWarningContext === CONTENT_MAP_WARNING_CONTEXT.LEARNING_PROGRESS
    ? t("content_map_not_enabled_for_learning_progress_banner_title")
    : t("content_map_not_enabled_for_moe_library_banner_title");
});

const contentMapWarningSubtitle = computed(() => {
  return props.contentMapWarningContext === CONTENT_MAP_WARNING_CONTEXT.LEARNING_PROGRESS
    ? t("content_map_not_enabled_for_learning_progress_banner_subtitle")
    : t("content_map_not_enabled_for_moe_library_banner_subtitle");
});

const showCognitiveLevel = computed(() => {
  return isSyllabusSelected.value && !isEmpty(cognitiveLevels.value);
});

const isTopicsEmpty = computed(() => {
  return isEmpty(topicsProcessed.value);
});

const syllabusSelected = computed(() => {
  return taggingSelectionLocal.value.syllabusSelected;
});

const cognitiveLevelSelected = computed(() => {
  return taggingSelectionLocal.value.cognitiveLevelSelected;
});

const topicsSelected = computed(() => {
  return taggingSelectionLocal.value.topicsSelected;
});

const contentMapMetadataSelected = computed(() => {
  return taggingSelectionLocal.value.contentMapMetadataSelected
    ? taggingSelectionLocal.value.contentMapMetadataSelected
    : {};
});

const showCognitiveLevelSection = computed(() => {
  return (
    props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.QUESTION ||
    props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.RUBRIC
  );
});

const resourceTaggingTopics = computed(() => {
  if (!isEmpty(props.resourceTagging) && !isEmpty(props.resourceTagging.taggings)) {
    return props.resourceTagging.taggings.filter((tagging) => {
      return !isDefined(tagging.cognitiveLevelId) || isNull(tagging.cognitiveLevelId);
    });
  }

  return [];
});

const contentMapSelections = computed(() => {
  const selectedSubjectLevels =
    resourceTaggingSelectionStore.value.getSubjectAndLevelTaggingsByResource(resourceUuid.value);

  const contentMapSelections = [];
  const selectedContentMaps = resourceTaggingSelectionStore.value.getContentMapTaggingsByResource(
    resourceUuid.value,
  );
  const selectedContentMapUuid = [];
  selectedContentMaps.forEach((selectedContentMap) => {
    if (selectedContentMap.contentMapMetadata) {
      selectedContentMapUuid.push(selectedContentMap.contentMapMetadata.uuid);
    }
  });
  selectedSubjectLevels.forEach((selectedSubjectLevel) => {
    if (!isEmpty(selectedSubjectLevel.subject.id)) {
      const subjectMetadata = taggingStore.value.getSubject(
        selectedSubjectLevel.subject.id.toString(),
      );

      if (!subjectMetadata || !subjectMetadata.levelAndStreams) {
        return;
      }

      const levelAndStreamId = !isEmpty(selectedSubjectLevel.levelAndStream)
        ? selectedSubjectLevel.levelAndStream.id
        : "";
      if (isEmpty(levelAndStreamId)) {
        return;
      }

      const subjectLevel = subjectMetadata.levelAndStreams.find(
        (levelAndStream) => levelAndStream.id === levelAndStreamId,
      );

      if (!subjectLevel || !subjectLevel.contentMaps) {
        return;
      }
      const contentMapsToBeAdded = subjectLevel.contentMaps.filter((contentMap) => {
        const filterAlsContentMap = props.isAdaptiveSection ? contentMap.als === true : true;

        return (
          filterAlsContentMap &&
          (contentMap.uuid === syllabusSelectedUI.value || // currently selected content map
            !selectedContentMapUuid.includes(contentMap.uuid)) // exclude other selected content map taggings
        );
      });

      if (!isEmpty(contentMapsToBeAdded)) {
        contentMapSelections.push({
          name: `${subjectMetadata.subjectName} - ${subjectLevel.displayName}`,
          label: `${subjectMetadata.subjectName} - ${subjectLevel.displayName}`,
          value: `${selectedSubjectLevel.tagId}`,
          header: true,
        });
      }

      contentMapsToBeAdded.forEach((contentMap) => {
        const syllabusName = contentMap.syllabus;
        let syllabusLabel = contentMap.title;
        if (!isEmpty(syllabusName)) {
          syllabusLabel = syllabusLabel.concat(` - ${syllabusName}`);
        }
        contentMap.displayName = syllabusLabel;
      });

      const sortConfig = [{ field: "displayName", asc: true }];
      const sortedContentMaps = sortUtils.sortObjects(contentMapsToBeAdded, sortConfig);

      sortedContentMaps.forEach((contentMap) => {
        contentMapSelections.push({
          name: contentMap.displayName,
          label: contentMap.displayName,
          value: contentMap.uuid.toString(),
        });
      });
    }
  });
  return contentMapSelections;
});

const useCheckBox = computed(() => {
  return props.allowMultipleSelectTopic;
});

const useRadio = computed(() => {
  return !props.allowMultipleSelectTopic;
});

// Same id precedence as the pre-populate step in processTopics (learning outcome first, then node)
const selectedRadioNodeId = computed(() => {
  if (!useRadio.value || isEmpty(topicsSelected.value)) {
    return null;
  }
  const [firstTopic] = topicsSelected.value;
  return firstTopic.learningOutcomeId || firstTopic.nodeId || null;
});

const syllabusLabel = computed(() => {
  if (props.isAdaptiveSection) {
    return t("adaptive_learning_content_map_label");
  } else {
    return t("resource_tagging_label_syllabus");
  }
});

const topicsLabel = computed(() => {
  if (props.isAdaptiveSection) {
    return t("adaptive_section_learning_outcome_label");
  } else {
    return t("resource_tagging_label_subtopic");
  }
});

const onlyLeafSelectable = computed(() => {
  return !(props.isAdaptiveSection && useCheckBox.value);
});

// Methods
const fetchSelectedContentMapUsage = async (contentMapUuid: string) => {
  selectedContentMapUsage.value = null;

  if (!props.contentMapWarningContext || isEmpty(contentMapUuid)) {
    return;
  }

  try {
    const response = await contentMapApi.getContentMap(contentMapUuid);

    if (syllabusSelected.value !== contentMapUuid) {
      return;
    }

    if (response.code === "ok") {
      selectedContentMapUsage.value = {
        usedInLearningProgress: response.data.usedInLearningProgress,
        usedInMoeLibrary: response.data.usedInMoeLibrary,
      };
    }
  } catch (error) {
    if (syllabusSelected.value === contentMapUuid) {
      passiveApiFailHandler(error);
    }
  }
};

const populateTopicsDataToRenderTree = function () {
  const setExpand = (topic) => {
    const parentTopic = topicsMap[topic.parentNodeId];
    if (parentTopic) {
      parentTopic["expanded"] = true;
      setExpand(parentTopic);
    }
  };
  let rootNodeId = 0;
  const topicsMap = topics.value.reduce(function (map, json) {
    const obj = JSON.parse(JSON.stringify(json));
    if (obj.parentNodeId === 0) {
      rootNodeId = obj.id;
    }
    map[obj.id] = obj;
    return map;
  }, {});

  // pre-populate selected node or learning outcome
  const selectedTopics = topicsSelected.value;
  if (!isEmpty(selectedTopics)) {
    selectedTopics.forEach((topicSelected) => {
      const topic = topicsMap[topicSelected.learningOutcomeId]
        ? topicsMap[topicSelected.learningOutcomeId]
        : topicsMap[topicSelected.nodeId];

      if (topic) {
        topic["checked"] = true;
        setExpand(topic);
      }
    });
  }
  let newTopics = [];
  Object.keys(topicsMap).forEach((key) => {
    if (topicsMap[key].parentNodeId === rootNodeId) {
      // remove parentNodeId or root node
      topicsMap[key].parentNodeId = 0;
    }
    if (rootNodeId !== parseInt(key)) {
      //exclude rootNode
      const topic = topicsMap[key];
      topic.data = {
        isLearningOutcome: topic.type === "LEARNING_OUTCOME",
        iconName:
          topic.type === "LEARNING_OUTCOME"
            ? topic.hasLearningLevel
              ? "LearningOutcomeAssistant24"
              : "LearningOutcome24"
            : "",
        cssClasses: { "icon-ai-colored": topic.hasLearningLevel },
      };
      newTopics.push(topic);
    }
  });
  newTopics = newTopics.sort((a, b) => {
    return a.type < b.type ? -1 : 1;
  });
  topicsProcessed.value = [];
  const nestedTopics = treeUtils.processDataForResourceTagging(newTopics);

  // sort syllabus on top
  const syllabuses = [],
    contentMapNodes = [];
  nestedTopics.forEach((topic) => {
    if (topic.type === "LEARNING_OUTCOME") syllabuses.push(topic);
    if (topic.type === "CONTENT_MAP_NODE") contentMapNodes.push(topic);
  });
  setHasAtLeastOneLoWithName(contentMapNodes);
  topicsProcessed.value = [...syllabuses, ...contentMapNodes];
};

const setHasAtLeastOneLoWithName = function (contentMapNodes) {
  hasAtLeastOneLoWithName.value = false; //reset to false
  hasAtLeastOneLoWithName.value = checkIfAtLeastOneNodeHasName(contentMapNodes);
};

const handleChangeSyllabus = function (value) {
  if (syllabusSelected.value === value) {
    return; //combo box need to check if selecting the same
  }

  syllabusSelectedUI.value = value;
  if (!isEmpty(topicsSelected.value)) {
    showResourceTaggingChangeConfirmationModal().then((resolve) => {
      if (resolve) {
        changeSyllabus(value);
      } else {
        // revert UI selection
        syllabusSelectedUI.value = syllabusSelected.value;
      }
    });
  } else {
    changeSyllabus(value);
  }
};

const changeSyllabus = function (value) {
  // reset selection
  topics.value = [];
  cognitiveLevels.value = [];
  taggingSelectionLocal.value.syllabusSelected = value;
  taggingSelectionLocal.value.topicsSelected = {};
  taggingSelectionLocal.value.cognitiveLevelSelected = "";
  taggingSelectionLocal.value.contentMapMetadataSelected = {};
  emit("update:taggingSelection", taggingSelectionLocal.value);
  emit("ChangeSelection");
};

const handleChangeTopics = function () {
  const isChanged = isTreeTopicSelectedChanged();
  if (!isChanged) {
    return;
  }

  // reset selection
  taggingSelectionLocal.value.topicsSelected = getTreeTopicsSelected();

  topicsIsDirty.value = true;
  emit("update:taggingSelection", taggingSelectionLocal.value);
  emit("ChangeSelection");
};

const getTreeTopicsSelected = function () {
  if (!isDefined(treeRef) || isNull(treeRef.value)) {
    return [];
  }
  const checkedNodes = treeRef.value.getCheckedLeafNodes();
  const distinctCheckedNodes = checkedNodes.filter(
    (node, index, array) => array.findIndex((value) => value.id === node.id) === index,
  );
  return distinctCheckedNodes.map((treeNode) => populateTopicDataToUpdate(treeNode));
};

const isTreeTopicSelectedChanged = function () {
  if (!isDefined(treeRef) || isNull(treeRef.value)) {
    return false;
  }

  const checkedNodes = treeRef.value.getCheckedLeafNodes();
  const topicsSelected = taggingSelectionLocal.value.topicsSelected;

  // check selection changed
  let isChanged = true;
  if (!isEmpty(topicsSelected) && topicsSelected.length === checkedNodes.length) {
    let count = 0;

    for (const nodeIndex in checkedNodes) {
      for (const topicIndex in topicsSelected) {
        if (
          checkedNodes[nodeIndex].id === topicsSelected[topicIndex].nodeId ||
          checkedNodes[nodeIndex].id === topicsSelected[topicIndex].learningOutcomeId
        ) {
          count++;
          break;
        }
      }
    }

    //check if all match
    if (count === topicsSelected.length) {
      isChanged = false;
    }
  } else {
    isChanged = true;
  }

  return isChanged;
};

const handleChangeCognitiveLevel = function (value) {
  if (cognitiveLevelSelectedUI.value !== value) {
    cognitiveLevelSelectedUI.value = value;
    taggingSelectionLocal.value.cognitiveLevelSelected = value;
    cognitiveLevelIsDirty.value = true;
    emit("update:taggingSelection", taggingSelectionLocal.value);
    emit("ChangeSelection");
  }
};

const showResourceTaggingChangeConfirmationModal = function () {
  return new Promise((resolve) => {
    const props = {
      title: t("resource_tagging_update_confirmation_title"),
      content: t("resource_tagging_update_confirmation_message"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      actionPrimary: () => {
        resolve(true);
      },
      actionSecondary: () => {
        resolve(false);
      },
    };
    modal.showMessage("success", props);
  });
};

const populateTopicDataToUpdate = function (treeNode) {
  const topicToUpdate = {};
  topics.value.forEach((topic) => {
    if (topic.id === treeNode.id) {
      if (topic.type === "CONTENT_MAP_NODE") {
        topicToUpdate.nodeId = treeNode.id;
        topicToUpdate.text = treeNode.title;
      } else if (topic.type === "LEARNING_OUTCOME") {
        topicToUpdate.learningOutcomeId = treeNode.id;
        topicToUpdate.text = treeNode.title;

        // retain cognitiveLevel
        const cognitiveLevel = getPreviousTaggedCognitiveLevel(
          contentMapMetadata.value.uuid,
          treeNode.id,
        );

        Object.assign(topicToUpdate, {
          cognitiveLevelId: cognitiveLevel.cognitiveLevelId,
          cognitiveLevelText: cognitiveLevel.cognitiveLevelText,
        });
      }
    }
  });

  return topicToUpdate;
};

const getPreviousTaggedCognitiveLevel = function (contentMapUuid, learningOutcomeId) {
  let cognitiveLevel = {
    cognitiveLevelId: null,
    cognitiveLevelText: null,
  };

  if (!isEmpty(taggingOri.value) && !isEmpty(taggingOri.value.taggings)) {
    taggingOri.value.taggings.forEach((tagging) => {
      if (tagging.learningOutcomeId == learningOutcomeId) {
        cognitiveLevel = {
          cognitiveLevelId: tagging.cognitiveLevelId,
          cognitiveLevelText: tagging.cognitiveLevelText,
        };
      }
    });
  }

  return cognitiveLevel;
};

const initSelectionData = function () {
  const contentMap = taggingStore.value.getContentMap(syllabusSelected.value);
  if (!isDefined(contentMap)) {
    taggingSelectionLocal.value.syllabusSelected = "";
    taggingSelectionLocal.value.topicsSelected = {};
    taggingSelectionLocal.value.cognitiveLevelSelected = "";
    taggingSelectionLocal.value.contentMapMetadataSelected = {};
    emit("update:taggingSelection", taggingSelectionLocal.value);
    emit("UpdateTagging");
    return;
  }

  syllabusSelectedUI.value = syllabusSelected.value;
  taggingSelectionLocal.value.syllabusSelected = syllabusSelected.value;
  void fetchSelectedContentMapUsage(syllabusSelected.value);
  //fetch content map topic data
  treeIsLoading.value = true;
  contentMapApi.fetchContentMapAndTopics(syllabusSelected.value).then((response) => {
    if (response.code === "ok") {
      topics.value = response.data.topics;
      cognitiveLevels.value = response.data.cognitiveLevels;
      taggingSelectionLocal.value.contentMapMetadataSelected.topics = response.data.topics;
      populateTopicsDataToRenderTree();

      nextTick(() => {
        cognitiveLevelSelectedUI.value = cognitiveLevelSelected.value;
        taggingSelectionLocal.value.topicsSelected = getTreeTopicsSelected();
        emit("update:taggingSelection", taggingSelectionLocal.value);
        emit("UpdateTagging");
      });
    }
  }, passiveApiFailHandler);
};

// Watch
watch(
  () => props.taggingSelection,
  function () {
    taggingSelectionLocal.value = props.taggingSelection;
  },
);

watch(syllabusSelected, function () {
  void fetchSelectedContentMapUsage(syllabusSelected.value);
  emit("update:taggingSelection", taggingSelectionLocal.value);
  emit("UpdateTagging");

  if (!isEmpty(syllabusSelected.value)) {
    //fetch content map topic data
    if (!isEmpty(topics.value) && contentMapMetadata.value.uuid === syllabusSelected.value) {
      return false;
    }

    treeIsLoading.value = true;
    contentMapApi.fetchContentMapAndTopics(syllabusSelected.value).then((response) => {
      //OK
      if (response.code === "ok") {
        const contentMap = response.data;

        taggingSelectionLocal.value.contentMapMetadataSelected = contentMap;
        emit("update:taggingSelection", taggingSelectionLocal.value);
        emit("ChangeSelection");
        emit("UpdateTagging");

        topics.value = response.data.topics;
        cognitiveLevels.value = response.data.cognitiveLevels;
        populateTopicsDataToRenderTree();
      }
    }, passiveApiFailHandler);
  }
});

watch(
  () => props.contentMapWarningContext,
  () => {
    void fetchSelectedContentMapUsage(syllabusSelected.value);
  },
);

onBeforeMount(() => {
  watch(topicsSelected, function () {
    // populate data to update if got selection changes (check or uncheck topics tree)
    if (topicsIsDirty.value) {
      emit("update:taggingSelection", taggingSelectionLocal.value);
      emit("UpdateTagging");
    }
  });
});

watch(cognitiveLevelSelected, function () {
  if (cognitiveLevelIsDirty.value) {
    emit("update:taggingSelection", taggingSelectionLocal.value);
    emit("UpdateTagging");
  }
});

watch(
  topicsProcessed,
  function () {
    treeIsLoading.value = false;
  },
  { deep: true },
);

watch(
  () => props.resourceTagging,
  function () {
    if (resourceTaggingTopics.value.length > 0 && isDefined(treeRef)) {
      const checkedTreeNodesLength = treeRef.value.getCheckedLeafNodes().length;
      const newTopicsLength = resourceTaggingTopics.value.length;
      if (newTopicsLength !== checkedTreeNodesLength) {
        // 1. Question copy taggings : there is topic selected merge from lesson taggings (tree is already render)
        // 2. Search retain taggings selected
        // reload tree
        taggingSelectionLocal.value.topicsSelected = resourceTaggingTopics;
        emit("update:taggingSelection", taggingSelectionLocal.value);
        emit("ChangeSelection");
        const isChanged = isTreeTopicSelectedChanged();
        if (isChanged) {
          treeIsLoading.value = true;
          populateTopicsDataToRenderTree();
          nextTick(() => {
            reload.value = "reload" + Math.random().toString();
          });
        }
      }
    }
  },
);

// Created
loadLocaleMessages("content-map").then(() => {
  initSelectionData();

  // pre resource tagging
  taggingOri.value = JSON.parse(JSON.stringify(props.resourceTagging));
  isLoading.value = false;
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.resource-tagging-selection {
  .field-set {
    &.type-radio {
      .field-input {
        background-color: $ui-01;
      }

      .input-radio {
        margin: $spacing-01 0;

        &:first-child {
          margin-top: 0;
        }
      }
    }

    &.topic {
      .field-label {
        @include carbon--type-style("body-short-02", true);
        color: $text-01;
      }

      .info {
        color: $text-02;
      }
    }
  }

  .content-map-warning {
    margin: 0 0 $spacing-05;
    width: 100%;
  }

  .helper-text {
    @include carbon--type-style("helper-text-01", true);
    margin-bottom: $spacing-05;
    color: $text-02;

    .bx--link {
      @include carbon--type-style("helper-text-01", true);

      .non-highlightable {
        margin-left: $spacing-02;
      }
    }

    ul {
      list-style: disc;
      margin-left: $spacing-06;
    }

    .field-set {
      margin-top: $spacing-03;

      .field-label {
        @include carbon--type-style("helper-text-01", true);
      }

      .field-value {
        @include carbon--type-style("helper-text-01", true);
        margin-top: 0;
        color: $text-02;
      }
    }
  }

  .tree {
    > .tree-list {
      > .tree-row {
        margin-bottom: $spacing-05;
      }
    }
  }

  .topic.tree {
    overflow: initial;

    .node-container {
      .input-radio {
        margin: 0;
      }

      .sls-icon {
        align-self: flex-start;
        min-width: 24px;
        margin-right: $spacing-03;
      }

      .rich-text {
        overflow-x: unset;
      }
    }

    .field-set {
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
    }

    .field-input {
      flex: 1 1 0%;
      display: flex;
      flex-flow: column nowrap;
      background-color: $ui-01;
    }

    .info {
      margin-bottom: $spacing-05;
      color: $text-03;
      @include carbon--type-style("helper-text-01", true);
    }

    .als-recommendation-legend {
      display: flex;
      align-items: center;
      gap: $spacing-03;
      margin: $spacing-03 0;

      > span {
        @include carbon--type-style("card-label-01");
        color: $text-03;
      }

      .sls-icon {
        min-width: 24px;
      }
    }

    .message {
      @include carbon--type-style("body-long-01", true);
    }

    .tree-wrapper.empty {
      flex: 1 1 0%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: $spacing-06;
      background-color: $ui-background;
      border-radius: 10px;
      color: $disabled-02;
      text-align: center;
    }
  }
}
</style>
